import React, {PureComponent} from "react"
import styled from "styled-components"
import moment from "moment"
import {debounce} from "lodash"

import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"

import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H2, Body, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {Hr} from "@irati/ui/src/Layout"
import {RenewalOption, InputRenewalProduct, RenewalProduct, ZuoraProductOptions, BillingCtaScreen} from "@irati/types"
import salesService from "@irati/sales/services/sales"
import authentication from "@irati/auth"
import buService from "@irati/billing/services/bu"
import usersService from "@irati/identity/services/users"
import Loading from "@irati/ui/src/Loading"
import Validation from "@irati/utils/validation"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSubSource, RenewalPlanSource} from "@irati-tracking/admin/admin"
import GenericModal from "@irati/ui/src/GenericModal"
import {CurrencyType} from "@irati/payments/enums"
import ErrorText from "@irati/ui/src/ErrorText"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {IconTypes} from "@irati/ui/src/Icon"

import mobileSchema from "../mobileSchema"
import RenewalHeader, {AddOns} from "../../Renewal/Header"
import Detail from "../../Renewal/Detail"
import {getAddOns} from "../CurrentPlan"
import RenewalsProgressBanner from "../../Renewal/RenewalsProgressBanner"
import NewRenewalCard from "../../NewRenewalCard"
import getPlan from "./getPlan"
import NewRenewalCards from "./NewRenewalCards"
import NewPlanCard from "./NewPlanCard"
import {MEDICAL_PRODUCT, FLEXI, FLEXI_POINTS_PREFIX, EAP_PRODUCT, NO_EAP} from "./constants"

interface Props extends InjectIntl {
  winbackOption?: RenewalOption
  onCallback: (subSource: RenewalPlanSubSource) => void
  onTimeout: () => void
  subSource: RenewalPlanSubSource
  onBack: () => void
  source: RenewalPlanSource
}

interface State {
  showConfirmRenewalDetails: boolean
  firstName?: string
  lastName?: string
  email?: string
  telephone?: string
  renewalDate?: string
  selectedQuote?: RenewalOption
  addOns: AddOns[]
  quotes?: RenewalOption[]
  etag?: string
  submit?: Promise<any> | null
  load?: Promise<any> | null
  currencyCode?: CurrencyType
  showConfirmationModal: boolean
  showAddMoreUsersModal: boolean
  error: boolean
  seats: number
  flexiPoints: number
  previewLoad: Promise<any> | null
  inputError: boolean
  products: RenewalProduct[]
  currentFlexiProduct: RenewalProduct
  currentEAPProduct: RenewalProduct
  flexiUpgrade: RenewalProduct
  EAPUpgrade?: RenewalProduct
  originalSeats: number
  ukSeats: number
  originalUkSeats: number
  renewalSeats: number
  productOptions: ZuoraProductOptions
}

const Width = styled.div`
  width: 100%;
`

class RenewalQuoteSelection extends PureComponent<Props, State> {
  state: State = {
    selectedQuote: undefined,
    quotes: [],
    showConfirmRenewalDetails: false,
    showAddMoreUsersModal: false,
    products: [],
    addOns: [],
    load: null,
    showConfirmationModal: false,
    error: false,
    seats: 0,
    ukSeats: 0,
    originalSeats: 0,
    originalUkSeats: 0,
    renewalSeats: 0,
    flexiPoints: 0,
    previewLoad: null,
    inputError: false,
    flexiUpgrade: null,
    EAPUpgrade: null
  }

  componentDidMount() {
    const businessId = authentication.business().uuid
    const {subSource, winbackOption, source, formatMessage} = this.props

    const load = Promise.all([
      buService.getCurrentPlan({businessId}),
      salesService.getRenewalQuote(businessId),
      buService.getProductOptions({businessId}),
      usersService.getUsersForBusiness({businessId, enabled: true, includeBusiness: false, includeIntegrations: false, totalOnly: true})
    ]).then(
      ([{endDate}, {options, etag: renewalEtag, products, seats: renewalSeats, signatureContact, currency}, productOptions, {total}]) => {
        salesService.notifyCTAScreenSeen({ctaScreen: BillingCtaScreen.CtaRenewal})

        const currentFlexiProduct = products?.find((product: RenewalProduct) => product.name.includes(FLEXI_POINTS_PREFIX))
        const currentEAPProduct = products?.find((product: RenewalProduct) => product.name.includes(EAP_PRODUCT))
        const medicalProduct = products?.find((product: RenewalProduct) => product.name === MEDICAL_PRODUCT)

        let ukSeats = 0
        if (currentEAPProduct) {
          ukSeats = currentEAPProduct.seats
        } else if (medicalProduct) {
          ukSeats = medicalProduct.seats
        }

        const selectedQuote = options[1]
        adminTracking.renewalPlan({
          stage: 2,
          subSource,
          type: RenewalPlanType.renewalFlow,
          page: RenewalPlanPage.newplanStage,
          plan: getPlan(selectedQuote),
          source
        })
        const seats = Math.max(total || 0, renewalSeats)

        this.setState({
          renewalDate: moment(endDate).format("DD MMM YYYY"),
          etag: renewalEtag,
          addOns: getAddOns(products, renewalSeats, formatMessage),
          products,
          currentFlexiProduct,
          currentEAPProduct,
          flexiUpgrade: currentFlexiProduct,
          EAPUpgrade: currentEAPProduct,
          quotes: options,
          selectedQuote,
          firstName: signatureContact.firstName,
          lastName: signatureContact.lastName,
          email: signatureContact.email,
          telephone: signatureContact.telephone,
          showConfirmRenewalDetails: !!winbackOption,
          currencyCode: currency,
          originalQuote: options[0],
          seats,
          originalSeats: seats,
          ukSeats,
          originalUkSeats: ukSeats,
          renewalSeats,
          productOptions
        })

        if (total && total > renewalSeats) {
          this.handlePreview(total)
        }
      }
    )
    this.setState({load})
  }

  buildEmailSchema = () => {
    return Validation.string("platform.defaultValidation.required").email("platform.fields.email.valid").max(255)
  }

  handleUpdateSelected = (selectedQuote: RenewalOption) => {
    const {seats, ukSeats, flexiUpgrade, EAPUpgrade} = this.state
    adminTracking.renewalPlan({
      stage: 2,
      subSource: this.props.subSource,
      type: RenewalPlanType.renewalFlow,
      page: RenewalPlanPage.newplanStage,
      plan: getPlan(selectedQuote),
      acv: selectedQuote?.annualCost,
      changeSeats: seats,
      changeFP: flexiUpgrade?.name,
      changeLocal: ukSeats,
      EAPUpgrade: EAPUpgrade?.name,
      source: this.props.source
    })
    this.setState({selectedQuote})
  }

  onSubmit = () => {
    const fields = [this.state.firstName, this.state.lastName, this.state.email, this.state.telephone]
    const isValid = fields.every((field) => {
      return field && field !== ""
    })

    if (isValid) {
      this.setState({showConfirmationModal: true})
    } else {
      this.setState({error: true})
    }
  }

  handleRenewalSubmitted = () => {
    const {etag, selectedQuote, firstName, lastName, email, telephone, seats, flexiUpgrade, ukSeats, EAPUpgrade} = this.state
    const {winbackOption, subSource, onCallback, onTimeout, source} = this.props
    const signatureContact = {
      firstName,
      lastName,
      email,
      telephone
    }

    const updatedProducts = this.buildProductsUpdated()

    const variables = {
      businessId: authentication.business().uuid,
      option: winbackOption || selectedQuote,
      etag,
      signatureContact,
      seats,
      ...updatedProducts
    }

    adminTracking.renewalPlan({
      stage: 3,
      page: RenewalPlanPage.confirmplanStage,
      subSource: RenewalPlanSubSource.confirmDetails,
      type: RenewalPlanType.renewalFlow,
      plan: getPlan(selectedQuote),
      contactDetail: signatureContact,
      action: "submitRenewal",
      acv: selectedQuote?.annualCost,
      changeSeats: seats,
      changeFP: flexiUpgrade?.name,
      changeLocal: ukSeats,
      EAPUpgrade: EAPUpgrade?.name,
      source
    })

    const submit = salesService
      .acceptRenewalQuote(variables)
      .then(() => {
        this.setState({showConfirmationModal: false})
        onCallback(subSource)
      })
      .catch((err) => {
        const errorCode = err?.networkError?.errorData?.errorCode
        if (errorCode?.includes("SalesforceTimeout")) {
          this.setState({showConfirmationModal: false})
          onTimeout()
        } else {
          throw err
        }
      })
    this.setState({submit})
  }

  handleShowConfirmRenewalDetails = () => {
    const {seats, originalSeats, selectedQuote, flexiUpgrade, ukSeats, EAPUpgrade} = this.state
    adminTracking.renewalPlan({
      stage: 2,
      subSource: this.props.subSource,
      type: RenewalPlanType.renewalFlow,
      page: RenewalPlanPage.newplanStage,
      plan: getPlan(selectedQuote),
      action: "confirmDetails",
      acv: selectedQuote?.annualCost,
      changeSeats: seats,
      changeFP: flexiUpgrade?.name,
      changeLocal: ukSeats,
      EAPUpgrade: EAPUpgrade?.name,
      source: this.props.source
    })
    if (seats < originalSeats) {
      this.setState({showAddMoreUsersModal: true})
    } else {
      this.handleConfirmRenewalDetails()
    }
  }

  handleConfirmRenewalDetails = () => {
    const {formatMessage} = this.props
    const {products, seats, EAPUpgrade, flexiUpgrade} = this.state
    let addOns = getAddOns(products, seats, formatMessage)
    if (flexiUpgrade) {
      const flexiIndex = addOns.findIndex(({label}) => label.includes(FLEXI))
      if (flexiIndex > -1) {
        addOns[flexiIndex] = {...addOns[flexiIndex], label: flexiUpgrade.name}
      } else {
        addOns = [
          ...addOns,
          {
            icon: IconTypes.flexi,
            label: flexiUpgrade.name
          }
        ]
      }
    }
    const EAPIndex = addOns.findIndex(({label}) => label.includes(EAP_PRODUCT))
    if (EAPUpgrade) {
      if (EAPIndex > -1) {
        addOns[EAPIndex] = {...addOns[EAPIndex], label: EAPUpgrade.name}
      } else {
        addOns = [
          ...addOns,
          {
            icon: IconTypes.multiComments,
            label: EAPUpgrade.name
          }
        ]
      }
    } else if (EAPIndex > -1) {
      addOns = addOns.filter((_, i) => i !== EAPIndex)
    }
    this.setState({showAddMoreUsersModal: false, showConfirmRenewalDetails: true, addOns})
  }

  handleBackToPlan = () => {
    const {formatMessage} = this.props
    const {products, renewalSeats} = this.state
    const addOns = getAddOns(products, renewalSeats, formatMessage)
    this.setState({showConfirmRenewalDetails: false, addOns})
  }

  handleUpdateDetail = (editParamater: string, value: string) => {
    this.setState({error: false})
    const {selectedQuote, seats, flexiUpgrade, ukSeats, EAPUpgrade} = this.state
    adminTracking.renewalPlan({
      stage: 3,
      page: RenewalPlanPage.confirmplanStage,
      subSource: RenewalPlanSubSource.confirmDetails,
      type: RenewalPlanType.renewalFlow,
      plan: getPlan(selectedQuote),
      editParamater,
      changeSeats: seats,
      changeFP: flexiUpgrade?.name,
      changeLocal: ukSeats,
      EAPUpgrade: EAPUpgrade?.name,
      source: this.props.source
    })
    this.setState({[editParamater]: value})
  }

  buildProductsUpdated = () => {
    const {flexiUpgrade, EAPUpgrade, currentFlexiProduct, currentEAPProduct, seats, ukSeats, products} = this.state
    let productsUpdated: InputRenewalProduct[] = []
    let productsAdded: InputRenewalProduct[] = []
    let productsRemoved: InputRenewalProduct[] = []

    if (currentFlexiProduct) {
      if (currentFlexiProduct?.id !== flexiUpgrade?.id) {
        productsRemoved = [currentFlexiProduct]
        productsAdded = [{...flexiUpgrade, seats}]
      } else if (currentFlexiProduct.seats !== seats) {
        productsUpdated = [{...flexiUpgrade, seats}]
      }
    } else if (flexiUpgrade && seats && seats !== 0) {
      productsAdded = [{...flexiUpgrade, seats}]
    }

    if (currentEAPProduct) {
      if (currentEAPProduct?.id !== EAPUpgrade?.id) {
        productsRemoved = [...productsRemoved, currentEAPProduct]
        if (EAPUpgrade) {
          productsAdded = [...productsAdded, {...EAPUpgrade, seats: ukSeats}]
        }
      } else if (currentEAPProduct.seats !== ukSeats) {
        productsUpdated = [...productsUpdated, {...EAPUpgrade, seats: ukSeats}]
      }
    } else if (EAPUpgrade && ukSeats && ukSeats !== 0) {
      productsAdded = [...productsAdded, {...EAPUpgrade, seats: ukSeats}]
    }

    const medical = products?.find((product) => product.name === MEDICAL_PRODUCT)

    if (medical?.seats && ukSeats && medical.seats !== ukSeats) {
      productsUpdated = [...productsUpdated, {...medical, seats: ukSeats}]
    }

    return {productsUpdated, productsAdded, productsRemoved}
  }

  handlePreview = (seats: number) => {
    const {etag, selectedQuote, originalQuote} = this.state
    this.setState({seats})

    const updatedProducts = this.buildProductsUpdated()

    const variables = {
      businessId: authentication.business().uuid,
      option: originalQuote,
      etag,
      seats,
      ...updatedProducts
    }

    const previewLoad = salesService.previewRenewalQuote(variables).then((res) => {
      this.setState({
        etag: res.etag,
        selectedQuote: res.options.find(({termLength}) => termLength === selectedQuote?.termLength),
        quotes: res.options
      })
    })
    this.setState({previewLoad})
  }

  handlePreviewDebounce = debounce((seats: number) => {
    this.handlePreview(seats)
  }, 500)

  handleUpdateSeats = (seats: number, ukSeats: number) => {
    this.setState({seats, ukSeats, inputError: false})
    this.handlePreviewDebounce(seats)
  }

  handleUpdateFlexi = (value) => {
    if (!value) {
      this.setState({flexiUpgrade: this.state.currentFlexiProduct})
      return this.handlePreviewDebounce(this.state.seats)
    }
    const {id, name, costPerSeatMonth, pricePerSeatPerMonth, recurringChargeId, usageChargeId} = value

    const flexiProductUpgrade = {
      id,
      name,
      costPerSeatMonth: costPerSeatMonth || pricePerSeatPerMonth,
      recurringChargeId,
      usageChargeId
    }

    this.setState({
      flexiUpgrade: flexiProductUpgrade
    })
    this.handlePreviewDebounce(this.state.seats)
  }

  handleupdateEAP = (value) => {
    if (!value?.upgrade) {
      const EAPUpgrade = value.name !== NO_EAP ? this.state.currentEAPProduct : undefined
      this.setState({EAPUpgrade})
      return this.handlePreviewDebounce(this.state.seats)
    }
    const {id, name, costPerSeatMonth, pricePerSeatPerMonth, recurringChargeId, usageChargeId} = value.upgrade

    const EAPProductUpgrade = {
      id,
      name,
      costPerSeatMonth: costPerSeatMonth || pricePerSeatPerMonth,
      recurringChargeId,
      usageChargeId
    }

    this.setState({
      EAPUpgrade: EAPProductUpgrade
    })
    this.handlePreviewDebounce(this.state.seats)
  }

  render() {
    const {
      selectedQuote,
      showConfirmRenewalDetails,
      firstName,
      lastName,
      email,
      telephone,
      renewalDate,
      quotes,
      products,
      addOns,
      submit,
      load,
      currencyCode,
      showConfirmationModal,
      showAddMoreUsersModal,
      error,
      seats,
      originalSeats,
      flexiPoints,
      previewLoad,
      inputError,
      productOptions,
      flexiUpgrade,
      EAPUpgrade,
      ukSeats,
      originalUkSeats,
      renewalSeats
    } = this.state

    const {winbackOption, onBack, formatMessage} = this.props

    return (
      <>
        {!showConfirmRenewalDetails ? (
          <Loading load={load}>
            <RenewalsProgressBanner activeStep={1} />
            <Flex gap={theme.spacing.two}>
              <Width>
                <NewPlanCard
                  seats={seats}
                  originalSeats={originalSeats}
                  originalUkSeats={originalUkSeats}
                  renewalSeats={renewalSeats}
                  products={products}
                  flexiPoints={flexiPoints}
                  addOns={addOns}
                  onCallback={this.handleUpdateSeats}
                  onError={() => this.setState({inputError: true})}
                  productOptions={productOptions}
                  onUpdateFlexi={this.handleUpdateFlexi}
                  onUpdateEAP={this.handleupdateEAP}
                  flexiUpgrade={flexiUpgrade}
                  EAPUpgrade={EAPUpgrade}
                  EAPSeats={ukSeats}
                />
              </Width>
              <NewRenewalCards
                load={previewLoad}
                hasError={inputError}
                quotes={quotes}
                selectedQuote={selectedQuote}
                onUpdateSelected={this.handleUpdateSelected}
                currencyCode={currencyCode}
                onShowConfirmRenewalDetails={this.handleShowConfirmRenewalDetails}
              />
            </Flex>
            <Flex justifyContent="space-between" marginTop={2}>
              <Button tertiary onClick={onBack}>
                <FormattedMessage id="admin.billing.renewals.newPlan.backToCurrentPlan" />
              </Button>
            </Flex>
          </Loading>
        ) : (
          <>
            <RenewalsProgressBanner activeStep={2} />
            <Card bgColor={theme.colors.primary2} marginBottom={4} padding={5}>
              {seats < originalSeats && (
                <Alert
                  type={AlertTypes.warning}
                  title="admin.billing.renewals.newPlan.warningAlert.increaseSeats"
                  link="admin.billing.renewals.newPlan.warningAlert.editPlan"
                  onClickLink={() => this.setState({showConfirmRenewalDetails: false})}
                />
              )}

              <Flex justifyContent="space-between">
                <div>
                  <RenewalHeader
                    title="admin.billing.renewals.newPlan.title"
                    body="admin.billing.renewals.confirmDetails.body"
                    addOns={addOns}
                    seats={seats}
                  />
                </div>
                <NewRenewalCard
                  hideSelector
                  quote={winbackOption || selectedQuote}
                  oneYearQuote={winbackOption?.termLength === 12 || selectedQuote?.termLength === 12 ? undefined : quotes[0]}
                  currencyCode={currencyCode}
                  isRecommended={selectedQuote?.termLength === 24}
                />
              </Flex>
            </Card>
            <H2 marginBottom={1}>
              <FormattedMessage id="admin.billing.renewals.planDetails.title" />
            </H2>
            <Body color={theme.colors.greys.medium} marginBottom={3}>
              <FormattedMessage id="admin.billing.renewals.planDetails.body" />
            </Body>
            <Detail
              currentValue={firstName}
              title="admin.billing.renewals.planDetails.firstName.title"
              editText="admin.billing.renewals.planDetails.firstName.edit"
              validation={Validation.string("platform.defaultValidation.required").test(
                "notJustSpaces",
                "platform.defaultValidation.notJustSpaces",
                (value: string) => value && value.trim().length !== 0
              )}
              onUpdate={(value) => this.handleUpdateDetail("firstName", value)}
            />
            <Detail
              currentValue={lastName}
              title="admin.billing.renewals.planDetails.lastName.title"
              editText="admin.billing.renewals.planDetails.lastName.title"
              validation={Validation.string("platform.defaultValidation.required").test(
                "notJustSpaces",
                "platform.defaultValidation.notJustSpaces",
                (value: string) => value && value.trim().length !== 0
              )}
              onUpdate={(value) => this.handleUpdateDetail("lastName", value)}
            />
            <Detail
              currentValue={email}
              title="admin.billing.renewals.planDetails.email.title"
              editText="admin.billing.renewals.planDetails.email.edit"
              validation={this.buildEmailSchema()}
              onUpdate={(value) => this.handleUpdateDetail("email", value)}
            />
            <Detail
              currentValue={telephone}
              title="admin.billing.renewals.planDetails.phone.title"
              editText="admin.billing.renewals.planDetails.phone.edit"
              validation={mobileSchema(formatMessage)}
              onUpdate={(value) => this.handleUpdateDetail("telephone", value)}
            />
            <Detail isEditable={false} title="admin.billing.renewals.planDetails.renewal.title" currentValue={renewalDate} />
            <Hr />
            <Label>
              <FormattedMessage id="admin.billing.renewals.newPlan.mandatoryFields" />
            </Label>
            <Flex justifyContent="space-between" marginBottom={2} marginTop={2}>
              <Button tertiary onClick={this.handleBackToPlan}>
                <FormattedMessage id="admin.billing.renewals.newPlan.back" />
              </Button>
              <ValidationButton primary onClick={this.onSubmit}>
                <FormattedMessage id="admin.billing.renewals.newPlan.submit" />
              </ValidationButton>
            </Flex>
            {error && (
              <Flex justifyContent="flex-end">
                <ErrorText>
                  <FormattedMessage id="admin.billing.renewals.newPlan.fieldsError" />
                </ErrorText>
              </Flex>
            )}
          </>
        )}
        <GenericModal hideActions show={showConfirmationModal} onHide={() => this.setState({showConfirmationModal: false})}>
          <H2 marginBottom={1}>
            <FormattedMessage id="admin.billing.renewals.newPlan.modal.header" />
          </H2>
          <Body marginBottom={3}>
            <FormattedMessage
              id="admin.billing.renewals.newPlan.modal.body"
              values={{
                firstName: firstName,
                lastName: lastName,
                email: email
              }}
            />
          </Body>
          <Button primary onClick={this.handleRenewalSubmitted} load={submit}>
            <FormattedMessage id="admin.billing.renewals.newPlan.modal.confirm" />
          </Button>
          <Loading load={submit} useLoader={false} />
        </GenericModal>
        <GenericModal hideActions show={showAddMoreUsersModal} onHide={() => this.setState({showAddMoreUsersModal: false})}>
          <H2 marginBottom={1} textAlign="center">
            <FormattedMessage id="admin.billing.renewals.newPlan.addMoreUsersModal.title" />
          </H2>
          <Flex justifyContent="center" marginBottom={2}>
            <img src={formatMessage({id: "admin.billing.renewals.newPlan.addMoreUsersModal.image"})} />
          </Flex>
          <Body marginBottom={1} textAlign="center">
            <FormattedMessage id="admin.billing.renewals.newPlan.addMoreUsersModal.body1" />
          </Body>
          <Body marginBottom={3} textAlign="center">
            <FormattedMessage id="admin.billing.renewals.newPlan.addMoreUsersModal.body2" />
          </Body>
          <Flex justifyContent="flex-end">
            <Button tertiary onClick={() => this.setState({showAddMoreUsersModal: false})}>
              <FormattedMessage id="admin.billing.renewals.newPlan.addMoreUsersModal.editButton" />
            </Button>
            <Button marginLeft primary onClick={this.handleConfirmRenewalDetails}>
              <FormattedMessage id="admin.billing.renewals.newPlan.addMoreUsersModal.proceedButton" />
            </Button>
          </Flex>
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(RenewalQuoteSelection)
